/**
 * NonConformitaRistorazione — modulo NC per variant "ristorazione" (R52.12)
 *
 * Mockup approvato: /preview-nc-ristorazione.html
 *
 * Filosofia (definita dall'utente):
 * In un ristorante una NC è un "incidente da chiudere veloce". L'operatore deve:
 *   - vedere SUBITO cosa va sistemato (default: Aperte)
 *   - chiudere con 1-2 tap + breve azione correttiva
 *   - aprirne una manualmente in <30 secondi
 *
 * Layout:
 *  - Header navy + 3 contatori inline (Aperte / In corso / Chiuse oggi)
 *  - CTA "Nuova NC"
 *  - Chip stato (Aperte default · In corso · Chiuse · Tutte)
 *  - Search
 *  - Card cronologica con pallino gravità, badge area + "🤖 auto" se auto_source set
 *  - Tap card → modal "Risolvi NC" diretto (azione correttiva + responsabile + In corso/Chiudi)
 *
 * Modal "+ Nuova NC":
 *  - 7 chip area: 🌡 Temperature · 🧼 Pulizie · 🐜 Infestanti · 🚛 Ricevimenti · 🔥 Produzioni · 🗑 Scarti · ⋯ Altro
 *  - Descrizione*
 *  - 3 chip gravità: Bassa · Media · Alta
 *  - Data
 *
 * Backend (esistente):
 *  - GET    /api/non-conformita
 *  - POST   /api/non-conformita
 *  - PUT    /api/non-conformita/{id}   (per chiudere/in-corso)
 *  - DELETE /api/non-conformita/{id}
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { apiErrMsg } from "@/lib/apiErr";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertOctagon, Plus, X, Search, Trash2, CheckCircle2, Clock, Bot,
} from "lucide-react";

// 7 chip area approvate
const AREE_CORE = [
  { v: "temperature",  emoji: "🌡", label: "Temperature" },
  { v: "pulizie",      emoji: "🧼", label: "Pulizie" },
  { v: "infestanti",   emoji: "🐜", label: "Infestanti" },
  { v: "ricevimenti",  emoji: "🚛", label: "Ricevimenti" },
  { v: "produzioni",   emoji: "🔥", label: "Produzioni" },
  { v: "scarti",       emoji: "🗑", label: "Scarti" },
  { v: "altro",        emoji: "⋯",  label: "Altro" },
];

// Mappa per badge area su card (include anche aree legacy non-core)
const AREA_BADGE = {
  temperature: { emoji: "🌡", label: "Temperature", cls: "bg-amber-100 text-amber-700" },
  pulizie:     { emoji: "🧼", label: "Pulizie",     cls: "bg-sky-100 text-sky-700" },
  infestanti:  { emoji: "🐜", label: "Infestanti",  cls: "bg-rose-100 text-rose-700" },
  ricevimenti: { emoji: "🚛", label: "Ricevimenti", cls: "bg-emerald-100 text-emerald-700" },
  produzioni:  { emoji: "🔥", label: "Produzioni",  cls: "bg-orange-100 text-orange-700" },
  scarti:      { emoji: "🗑", label: "Scarti",      cls: "bg-slate-100 text-slate-700" },
  allergeni:   { emoji: "🌾", label: "Allergeni",   cls: "bg-yellow-100 text-yellow-700" },
  documenti:   { emoji: "📄", label: "Documenti",   cls: "bg-indigo-100 text-indigo-700" },
  etichette:   { emoji: "🏷", label: "Etichette",   cls: "bg-violet-100 text-violet-700" },
  igiene_personale: { emoji: "🧑‍🍳", label: "Igiene Personale", cls: "bg-teal-100 text-teal-700" },
  altro:       { emoji: "⋯",  label: "Altro",       cls: "bg-slate-100 text-slate-700" },
};

// 3 livelli (Bassa/Media/Alta) — backend ne ha 4 ma "critica" mappata su "alta"
const GRAVITA = [
  { v: "bassa", emoji: "🟢", label: "Bassa", dot: "bg-emerald-500", borderActive: "border-emerald-500 bg-emerald-100 text-emerald-800", idle: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  { v: "media", emoji: "🟡", label: "Media", dot: "bg-amber-500",   borderActive: "border-amber-500 bg-amber-100 text-amber-800",     idle: "border-amber-200 bg-amber-50 text-amber-700" },
  { v: "alta",  emoji: "🔴", label: "Alta",  dot: "bg-rose-500",    borderActive: "border-rose-500 bg-rose-100 text-rose-800",         idle: "border-rose-200 bg-rose-50 text-rose-700" },
];

function gravitaUI(g) {
  if (g === "critica") return GRAVITA[2];
  return GRAVITA.find(x => x.v === g) || GRAVITA[1];
}

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    const dN = new Date(d); dN.setHours(0, 0, 0, 0);
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    if (dN.getTime() === today.getTime()) return `Oggi ${time}`;
    if (dN.getTime() === yest.getTime()) return `Ieri ${time}`;
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  } catch { return iso; }
}

// Estrae titolo + sottotitolo dalla descrizione (le NC auto hanno descrizione lunga)
function splitDesc(desc) {
  if (!desc) return { title: "—", subtitle: "" };
  // Cerca un separatore tipico " — " usato da auto_source per separare titolo da dettagli
  const idx = desc.indexOf(" — ");
  if (idx > 0 && idx < 100) return { title: desc.slice(0, idx), subtitle: desc.slice(idx + 3) };
  if (desc.length > 60) return { title: desc.slice(0, 60), subtitle: desc.slice(60) };
  return { title: desc, subtitle: "" };
}

export default function NonConformitaRistorazione() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "operator" || user?.role === "superadmin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stato, setStato] = useState("aperta");  // default Aperte
  const [openNew, setOpenNew] = useState(false);
  const [resolving, setResolving] = useState(null); // NC da risolvere (tap card)
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const [draft, setDraft] = useState({
    area: "altro",
    descrizione: "",
    gravita: "media",
    data: new Date().toISOString().slice(0, 10),
  });
  const [resDraft, setResDraft] = useState({
    azione_correttiva: "",
    responsabile: "",
    note: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/non-conformita");
      setItems(data || []);
    } catch {
      toast.error("Errore caricamento NC");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let aperte = 0, incorso = 0, chiuseOggi = 0, chiuseTot = 0, totale = 0;
    for (const r of items) {
      totale++;
      if (r.stato === "aperta") aperte++;
      else if (r.stato === "in-corso") incorso++;
      else if (r.stato === "chiusa") {
        chiuseTot++;
        if ((r.chiusa_il || "").startsWith(today) || (r.updated_at || "").startsWith(today)) chiuseOggi++;
      }
    }
    return { aperte, incorso, chiuseOggi, chiuseTot, totale };
  }, [items]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return items
      .filter(r => {
        if (stato !== "tutte" && r.stato !== stato) return false;
        if (!q) return true;
        const hay = `${r.descrizione || ""} ${r.area || ""} ${r.responsabile || ""} ${r.note || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => (b.created_at || b.data || "").localeCompare(a.created_at || a.data || ""));
  }, [items, stato, search]);

  // ─── Apri "Nuova NC"
  const startNew = () => {
    setDraft({
      area: "altro",
      descrizione: "",
      gravita: "media",
      data: new Date().toISOString().slice(0, 10),
    });
    setOpenNew(true);
  };

  const createNc = async () => {
    if (!draft.descrizione.trim()) { toast.error("Descrizione obbligatoria"); return; }
    setSaving(true);
    try {
      await api.post("/non-conformita", {
        area: draft.area,
        descrizione: draft.descrizione.trim(),
        gravita: draft.gravita,
        stato: "aperta",
        data: draft.data,
      });
      toast.success("NC creata");
      setOpenNew(false);
      await load();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore creazione NC"));
    } finally { setSaving(false); }
  };

  // ─── Apri "Risolvi"
  const openResolve = (nc) => {
    setResolving(nc);
    setResDraft({
      azione_correttiva: nc.azione_correttiva || "",
      responsabile: nc.responsabile || (user?.name || user?.email || ""),
      note: nc.note || "",
    });
  };

  const saveResolve = async (newStato) => {
    if (!resolving) return;
    if (newStato === "chiusa" && !resDraft.azione_correttiva.trim()) {
      toast.error("Azione correttiva obbligatoria per chiudere");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...resolving,
        azione_correttiva: resDraft.azione_correttiva,
        responsabile: resDraft.responsabile,
        note: resDraft.note,
        stato: newStato,
        chiusa_il: newStato === "chiusa" ? new Date().toISOString() : (resolving.chiusa_il || null),
        updated_at: new Date().toISOString(),
      };
      // Rimuovi campi non Pydantic (es. _id se presente)
      delete payload._id;
      await api.put(`/non-conformita/${resolving.id}`, payload);
      toast.success(newStato === "chiusa" ? "NC chiusa" : "NC in corso");
      setResolving(null);
      await load();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore salvataggio"));
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/non-conformita/${toDelete.id}`);
      toast.success("NC eliminata");
      setToDelete(null);
      await load();
    } catch (e) { toast.error(apiErrMsg(e, "Errore eliminazione")); }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="nc-ristorazione">
      {/* HEADER */}
      <header className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-5 pt-5 pb-4 rounded-b-2xl shadow-lg">
        <div className="max-w-md lg:max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md shrink-0">
            <AlertOctagon className="w-6 h-6 text-[#13294B]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">e-Log Ristorazione</p>
            <h1 className="text-xl font-bold leading-tight">Non Conformità</h1>
          </div>
        </div>
      </header>

      {/* CTA */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 -mt-3">
        <button onClick={startNew} disabled={!canWrite}
                className="w-full bg-[#13294B] text-white rounded-2xl py-3.5 text-sm font-bold flex items-center justify-center gap-2 shadow-md active:scale-[.98] disabled:opacity-50"
                data-testid="open-new-btn">
          <Plus className="w-5 h-5" /> Nuova NC
        </button>
      </section>

      {/* Stat 3-cell */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3">
        <div className="bg-white rounded-2xl border border-slate-200 px-3 py-2 flex items-center justify-between text-[12px]" data-testid="stat-bar">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /><b>{counts.aperte}</b> aperte</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /><b>{counts.incorso}</b> in corso</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><b>{counts.chiuseOggi}</b> chiuse oggi</span>
        </div>
      </section>

      {/* Filtri stato */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3 space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#13294B]"
                 placeholder="Cerca descrizione, area…"
                 data-testid="search-input" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          <button onClick={() => setStato("aperta")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                    stato === "aperta" ? "bg-rose-600 text-white" : "bg-white text-rose-700 border border-rose-200"
                  }`}
                  data-testid="chip-aperte">Aperte ({counts.aperte})</button>
          <button onClick={() => setStato("in-corso")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                    stato === "in-corso" ? "bg-amber-600 text-white" : "bg-white text-amber-700 border border-amber-200"
                  }`}
                  data-testid="chip-incorso">In corso ({counts.incorso})</button>
          <button onClick={() => setStato("chiusa")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                    stato === "chiusa" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 border border-emerald-200"
                  }`}
                  data-testid="chip-chiuse">Chiuse ({counts.chiuseTot})</button>
          <button onClick={() => setStato("tutte")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                    stato === "tutte" ? "bg-[#13294B] text-white" : "bg-white text-slate-600 border border-slate-200"
                  }`}
                  data-testid="chip-tutte">Tutte ({counts.totale})</button>
        </div>
      </section>

      <main className="max-w-md lg:max-w-3xl mx-auto px-4 py-4">
        <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 px-1 mb-2">
          {stato === "aperta" ? "Aperte" : stato === "in-corso" ? "In corso" : stato === "chiusa" ? "Chiuse" : "Tutte"}{" "}
          <span className="normal-case text-slate-400 font-normal">({filtered.length})</span>
        </h2>

        {loading && <div className="text-center text-slate-500 text-sm py-8">Caricamento…</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="empty-state">
            {stato === "aperta" ? "Nessuna NC aperta. Bel lavoro!" : "Nessuna NC nel filtro selezionato."}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <ul className="space-y-2" data-testid="nc-list">
            {filtered.map(r => {
              const g = gravitaUI(r.gravita);
              const area = AREA_BADGE[r.area] || AREA_BADGE.altro;
              const isAlta = (r.gravita === "alta" || r.gravita === "critica");
              const isClosed = r.stato === "chiusa";
              const { title, subtitle } = splitDesc(r.descrizione);
              return (
                <li key={r.id}
                    onClick={() => !isClosed && openResolve(r)}
                    className={`bg-white rounded-2xl shadow-sm p-3 transition cursor-pointer ${
                      isAlta && !isClosed ? "border-2 border-rose-200" : "border border-slate-200"
                    } ${isClosed ? "opacity-60" : "active:scale-[.99]"}`}
                    data-testid={`nc-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${g.dot}`} title={g.label} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1 flex-wrap">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${area.cls}`}>
                          {area.emoji} {area.label}
                        </span>
                        {r.auto_source && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <Bot className="w-2.5 h-2.5" /> auto
                          </span>
                        )}
                        {isClosed && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded">✓ Chiusa</span>
                        )}
                        {r.stato === "in-corso" && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded">⏳ In corso</span>
                        )}
                      </div>
                      <p className="font-semibold text-sm text-slate-900 leading-tight">{title}</p>
                      {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
                      <p className="text-[10px] text-slate-400 mt-1">{fmtRel(r.created_at || r.data)}</p>
                    </div>
                    {canWrite && (
                      <button onClick={(e) => { e.stopPropagation(); setToDelete(r); }}
                              className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-rose-600 shrink-0"
                              data-testid={`del-${r.id}`}
                              aria-label="Elimina">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {/* ─── DIALOG NUOVA NC ─── */}
      <Dialog open={openNew} onOpenChange={(o) => { if (!o) setOpenNew(false); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="new-dialog">
          <DialogTitle className="sr-only">Nuova Non Conformità</DialogTitle>
          <DialogDescription className="sr-only">Registra una nuova non conformità manuale.</DialogDescription>

          <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-bold text-base">Nuova NC</h3>
            <button onClick={() => setOpenNew(false)}
                    className="w-9 h-9 rounded-full bg-white/25 hover:bg-white/35 border border-white/30 flex items-center justify-center text-white"
                    aria-label="Chiudi" data-testid="new-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* Area */}
            <div>
              <Label className="text-[11px] font-semibold mb-2 block">Area <span className="text-rose-600">*</span></Label>
              <div className="grid grid-cols-3 gap-1.5">
                {AREE_CORE.slice(0, 6).map(a => (
                  <button key={a.v} type="button"
                          onClick={() => setDraft({ ...draft, area: a.v })}
                          className={`px-2 py-2 rounded-xl text-[11px] font-semibold border transition ${
                            draft.area === a.v
                              ? "bg-[#13294B] text-white border-[#13294B]"
                              : "bg-white text-slate-700 border-slate-300"
                          }`}
                          data-testid={`area-${a.v}`}>
                    {a.emoji} {a.label}
                  </button>
                ))}
                <button type="button"
                        onClick={() => setDraft({ ...draft, area: "altro" })}
                        className={`px-2 py-2 rounded-xl text-[11px] font-semibold border transition col-span-3 ${
                          draft.area === "altro"
                            ? "bg-[#13294B] text-white border-[#13294B]"
                            : "bg-white text-slate-700 border-slate-300"
                        }`}
                        data-testid="area-altro">
                  ⋯ Altro
                </button>
              </div>
            </div>

            {/* Descrizione */}
            <div>
              <Label htmlFor="nc-desc" className="text-[11px] font-semibold">Descrizione <span className="text-rose-600">*</span></Label>
              <textarea id="nc-desc"
                        value={draft.descrizione}
                        onChange={(e) => setDraft({ ...draft, descrizione: e.target.value })}
                        placeholder="Es. Pavimento cucina non pulito a fine servizio"
                        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm h-16 resize-none"
                        data-testid="nc-desc" />
            </div>

            {/* Gravità */}
            <div>
              <Label className="text-[11px] font-semibold mb-1.5 block">Gravità</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {GRAVITA.map(g => (
                  <button key={g.v} type="button"
                          onClick={() => setDraft({ ...draft, gravita: g.v })}
                          className={`px-2 py-2.5 rounded-xl text-[11px] font-semibold border-2 transition ${
                            draft.gravita === g.v ? g.borderActive : g.idle
                          }`}
                          data-testid={`grav-${g.v}`}>
                    {g.emoji} {g.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Data */}
            <div>
              <Label htmlFor="nc-data" className="text-[11px] font-semibold">Data rilevazione</Label>
              <Input id="nc-data" type="date" value={draft.data}
                     onChange={(e) => setDraft({ ...draft, data: e.target.value })}
                     className="mt-1" data-testid="nc-data" />
            </div>
          </div>

          <div className="p-3 grid grid-cols-2 gap-2 border-t border-slate-200">
            <button onClick={() => setOpenNew(false)}
                    className="rounded-xl border border-slate-300 py-2.5 text-xs font-semibold"
                    data-testid="nc-cancel">Annulla</button>
            <button onClick={createNc} disabled={saving}
                    className="rounded-xl bg-[#13294B] text-white py-2.5 text-xs font-bold disabled:opacity-50"
                    data-testid="nc-save">
              {saving ? "Salvataggio…" : "Crea NC"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── DIALOG RISOLVI NC ─── */}
      <Dialog open={!!resolving} onOpenChange={(o) => { if (!o) setResolving(null); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="resolve-dialog">
          <DialogTitle className="sr-only">Risolvi NC</DialogTitle>
          <DialogDescription className="sr-only">Registra l&apos;azione correttiva e chiudi la non conformità.</DialogDescription>

          {resolving && (() => {
            const g = gravitaUI(resolving.gravita);
            const area = AREA_BADGE[resolving.area] || AREA_BADGE.altro;
            const { title, subtitle } = splitDesc(resolving.descrizione);
            return (
              <>
                <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center gap-2">
                  <h3 className="font-bold text-base flex-1">Risolvi NC</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                    g.v === "alta" ? "bg-rose-300 text-rose-900" :
                    g.v === "media" ? "bg-amber-300 text-amber-900" : "bg-emerald-300 text-emerald-900"
                  }`}>{g.label}</span>
                  <button onClick={() => setResolving(null)}
                          className="w-9 h-9 rounded-full bg-white/25 hover:bg-white/35 border border-white/30 flex items-center justify-center text-white"
                          aria-label="Chiudi" data-testid="resolve-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                  {/* Dettagli */}
                  <div className={`rounded-2xl p-3 border ${
                    g.v === "alta" ? "bg-rose-50 border-rose-200" :
                    g.v === "media" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
                  }`} data-testid="resolve-details">
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${area.cls}`}>{area.emoji} {area.label}</span>
                      {resolving.auto_source && (
                        <span className="text-[10px] bg-slate-100 text-slate-600 font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Bot className="w-2.5 h-2.5" /> auto
                        </span>
                      )}
                    </div>
                    <p className="font-bold text-sm text-slate-900">{title}</p>
                    {subtitle && <p className="text-[11px] text-slate-600 mt-1">{subtitle}</p>}
                  </div>

                  <div>
                    <Label htmlFor="r-az" className="text-[11px] font-semibold">Azione correttiva <span className="text-rose-600">*</span></Label>
                    <textarea id="r-az"
                              value={resDraft.azione_correttiva}
                              onChange={(e) => setResDraft({ ...resDraft, azione_correttiva: e.target.value })}
                              placeholder="Cosa hai fatto per risolvere?"
                              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm h-16 resize-none"
                              data-testid="r-azione" />
                  </div>

                  <div>
                    <Label htmlFor="r-resp" className="text-[11px] font-semibold">Responsabile</Label>
                    <Input id="r-resp" value={resDraft.responsabile}
                           onChange={(e) => setResDraft({ ...resDraft, responsabile: e.target.value })}
                           className="mt-1" data-testid="r-resp" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Pre-compilato con utente loggato</p>
                  </div>

                  <div>
                    <Label htmlFor="r-note" className="text-[11px] font-semibold">Note</Label>
                    <textarea id="r-note"
                              value={resDraft.note}
                              onChange={(e) => setResDraft({ ...resDraft, note: e.target.value })}
                              placeholder="Eventuali note interne"
                              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm h-12 resize-none"
                              data-testid="r-note" />
                  </div>
                </div>

                <div className="p-3 grid grid-cols-3 gap-2 border-t border-slate-200">
                  <button onClick={() => setResolving(null)}
                          className="rounded-xl border border-slate-300 py-2.5 text-[11px] font-semibold"
                          data-testid="r-cancel">Annulla</button>
                  <button onClick={() => saveResolve("in-corso")} disabled={saving}
                          className="rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-800 py-2.5 text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                          data-testid="r-incorso">
                    <Clock className="w-3 h-3" /> In corso
                  </button>
                  <button onClick={() => saveResolve("chiusa")} disabled={saving}
                          className="rounded-xl bg-emerald-600 text-white py-2.5 text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                          data-testid="r-chiudi">
                    <CheckCircle2 className="w-3 h-3" /> Chiudi NC
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ─── CONFIRM DELETE ─── */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la NC?</AlertDialogTitle>
            <AlertDialogDescription>
              La NC verrà eliminata definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">
              Sì, elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
